import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { GatewaysState, IGateway } from '../types';
import { ApiService } from '../services/api';
import { RootState } from './index';

// Helper function to create ApiService with current token
const createApiService = (getState: () => RootState) => {
    const state = getState();
    const apiService = new ApiService(
        process.env.REACT_APP_API_URL || 'http://localhost:3051',
        process.env.REACT_APP_API_URL || 'http://localhost:3051/api'
    );
    // Use token from Redux state, or fallback to localStorage if state hasn't been hydrated yet
    const token = state.auth.token || localStorage.getItem('token');
    if (token) {
        apiService.setToken(token);
    }
    return apiService;
};

interface GatewaysStateWithLoading extends GatewaysState {
    loading: boolean;
    error: string | null;
}

const initialState: GatewaysStateWithLoading = {
    entities: {},
    available: [],
    monitored: [],
    loading: false,
    error: null,
};

// Async thunks
export const fetchGateways = createAsyncThunk(
    'gateways/fetchGateways',
    async (_, { getState }) => {
        const apiService = createApiService(getState as () => RootState);
        return await apiService.fetchGateways();
    }
);

export const saveGateway = createAsyncThunk(
    'gateways/saveGateway',
    async (gateway: IGateway, { getState }) => {
        // console.log('🔄 saveGateway thunk called with:', gateway);
        const apiService = createApiService(getState as () => RootState);
        try {
            let result;
            if (gateway._id) {
                // console.log('📝 Updating existing gateway:', gateway.mac);
                result = await apiService.updateGateway(gateway.mac, gateway);
            } else {
                // console.log('➕ Creating new gateway:', gateway.mac);
                const { _id, createdAt, updatedAt, ...gatewayData } = gateway;
                result = await apiService.createGateway(gatewayData);
            }
            // console.log('✅ saveGateway API response:', result);
            return result;
        } catch (error) {
            // console.error('❌ saveGateway API error:', error);
            throw error;
        }
    }
);

export const removeGatewayAsync = createAsyncThunk(
    'gateways/removeGateway',
    async (mac: string, { getState }) => {
        const apiService = createApiService(getState as () => RootState);
        await apiService.deleteGateway(mac);
        return mac;
    }
);

// Hybrid system thunks
export const fetchAvailableGateways = createAsyncThunk(
    'gateways/fetchAvailable',
    async (lastSeenDays: number | null | undefined, { getState }) => {
        const apiService = createApiService(getState as () => RootState);
        return await apiService.fetchAvailableGateways(lastSeenDays || undefined);
    }
);

export const fetchMonitoredGateways = createAsyncThunk(
    'gateways/fetchMonitored',
    async (_, { getState }) => {
        const apiService = createApiService(getState as () => RootState);
        return await apiService.fetchMonitoredGateways();
    }
);

export const fetchMapGateways = createAsyncThunk(
    'gateways/fetchMapGateways',
    async (mapId: string, { getState }) => {
        const apiService = createApiService(getState as () => RootState);
        return await apiService.fetchMapGateways(mapId);
    }
);

export const addGatewayToMonitoring = createAsyncThunk(
    'gateways/addToMonitoring',
    async ({ mac, position, radius, mapId, settings }: {
        mac: string;
        position: { x: number; y: number };
        radius: number;
        mapId: string;
        settings?: { displayName?: string; color?: string; showCoverage?: boolean; logicThreshold?: number; maxRSSI?: number }
    }, { getState }) => {
        const apiService = createApiService(getState as () => RootState);
        return await apiService.addGatewayToMonitoring(mac, position, radius, settings, mapId);
    }
);

export const removeGatewayFromMonitoring = createAsyncThunk(
    'gateways/removeFromMonitoring',
    async (mac: string, { getState }) => {
        const apiService = createApiService(getState as () => RootState);
        return await apiService.removeGatewayFromMonitoring(mac);
    }
);

// Helper functions for localStorage persistence
const saveViewSettingsToLocal = (mac: string, settings: { viewRadius?: number; logicThreshold?: number }) => {
    try {
        const existing = localStorage.getItem('gatewayViewSettings');
        const allSettings = existing ? JSON.parse(existing) : {};
        allSettings[mac] = { ...allSettings[mac], ...settings };
        localStorage.setItem('gatewayViewSettings', JSON.stringify(allSettings));
    } catch (error) {
        // console.warn('Failed to save view settings to localStorage:', error);
    }
};

const loadViewSettingsFromLocal = (): { [mac: string]: { viewRadius?: number; logicThreshold?: number } } => {
    try {
        const stored = localStorage.getItem('gatewayViewSettings');
        return stored ? JSON.parse(stored) : {};
    } catch (error) {
        // console.warn('Failed to load view settings from localStorage:', error);
        return {};
    }
};

const gatewaysSlice = createSlice({
    name: 'gateways',
    initialState,
    reducers: {
        setGateways: (state, action: PayloadAction<{ [mac: string]: IGateway }>) => {
            state.entities = action.payload;
        },
        addGateway: (state, action: PayloadAction<IGateway>) => {
            state.entities[action.payload.mac] = action.payload;
        },
        updateGateway: (state, action: PayloadAction<IGateway>) => {
            // console.log('⚡ updateGateway reducer called with:', action.payload);
            const oldGateway = state.entities[action.payload.mac];
            // console.log('🔍 Old gateway in reducer:', oldGateway);
            state.entities[action.payload.mac] = action.payload;
            // console.log('🔍 New gateway in reducer:', state.entities[action.payload.mac]);

            // Settings are now persisted in backend, no need for localStorage
        },
        removeGateway: (state, action: PayloadAction<string>) => {
            delete state.entities[action.payload];

            // Clean up localStorage for this gateway
            try {
                const existing = localStorage.getItem('gatewayViewSettings');
                if (existing) {
                    const allSettings = JSON.parse(existing);
                    delete allSettings[action.payload];
                    localStorage.setItem('gatewayViewSettings', JSON.stringify(allSettings));
                }
            } catch (error) {
                // console.warn('Failed to clean up view settings from localStorage:', error);
            }
        },
        updateGatewayPosition: (state, action: PayloadAction<{ mac: string; position: { x: number; y: number } }>) => {
            const { mac, position } = action.payload;
            if (state.entities[mac]) {
                state.entities[mac].position = position;
            }
        },
        resetGatewaysState: (state) => {
            Object.assign(state, initialState);
        },
    },
    extraReducers: (builder) => {
        builder
            // Fetch gateways
            .addCase(fetchGateways.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchGateways.fulfilled, (state, action) => {
                state.loading = false;
                state.entities = {};

                // Settings are now persisted in backend, no need for localStorage merging
                // Map customSettings to top-level properties for consistency
                action.payload.forEach(gateway => {
                    const mappedGateway = {
                        ...gateway,
                        mapId: (gateway as any).mapId && typeof (gateway as any).mapId === 'object' && '_id' in (gateway as any).mapId ? (gateway as any).mapId._id : gateway.mapId,
                        viewRadius: gateway.customSettings?.viewRadius || gateway.viewRadius,
                        logicThreshold: gateway.customSettings?.logicThreshold || gateway.logicThreshold || gateway.maxRSSI,
                    };
                    state.entities[gateway.mac] = mappedGateway;
                });
            })
            .addCase(fetchMapGateways.fulfilled, (state, action) => {
                state.loading = false;

                // Map-specific gateways - merge with existing entities to keep all gateways available
                // This prevents devices from disappearing when their strongestGateway is on a different map
                action.payload.gateways.forEach(gateway => {
                    // Map customSettings to top-level properties for easier access
                    const mappedGateway = {
                        ...gateway,
                        mapId: (gateway as any).mapId && typeof (gateway as any).mapId === 'object' && '_id' in (gateway as any).mapId ? (gateway as any).mapId._id : gateway.mapId,
                        viewRadius: gateway.customSettings?.viewRadius || gateway.viewRadius,
                        logicThreshold: gateway.customSettings?.logicThreshold || gateway.logicThreshold || gateway.maxRSSI,
                    };
                    state.entities[gateway.mac] = mappedGateway;
                });
            })
            .addCase(fetchGateways.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || 'Failed to fetch gateways';
            })
            // Save gateway
            .addCase(saveGateway.pending, (state) => {
                // console.log('⏳ saveGateway pending...');
            })
            .addCase(saveGateway.fulfilled, (state, action) => {
                // console.log('✅ saveGateway fulfilled, updating state with:', action.payload);
                const oldGateway = state.entities[action.payload.mac];
                // console.log('🔍 Old gateway state:', oldGateway);
                // console.log('🔍 New gateway state:', action.payload);

                // Preserve local view values that might not be persisted to server
                const updatedGateway = {
                    ...action.payload,
                    // Preserve viewRadius if it exists in the old gateway but not in the response
                    viewRadius: action.payload.viewRadius !== undefined ? action.payload.viewRadius : oldGateway?.viewRadius,
                    // Preserve logicThreshold if it exists in the old gateway but not in the response
                    logicThreshold: action.payload.logicThreshold !== undefined ? action.payload.logicThreshold : oldGateway?.logicThreshold
                };

                state.entities[action.payload.mac] = updatedGateway;
                // console.log('🔄 Gateway state updated. Current entities:', Object.keys(state.entities));
                // console.log('🔍 Updated gateway in state:', state.entities[action.payload.mac]);
            })
            .addCase(saveGateway.rejected, (state, action) => {
                // console.error('❌ saveGateway rejected:', action.error);
                state.error = action.error.message || 'Failed to save gateway';
            })
            // Remove gateway
            .addCase(removeGatewayAsync.fulfilled, (state, action) => {
                delete state.entities[action.payload];
            })
            // Hybrid system cases
            .addCase(fetchAvailableGateways.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchAvailableGateways.fulfilled, (state, action) => {
                state.loading = false;
                state.available = action.payload.gateways || [];
            })
            .addCase(fetchAvailableGateways.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || 'Failed to fetch available gateways';
            })
            .addCase(fetchMonitoredGateways.fulfilled, (state, action) => {
                state.monitored = action.payload.gateways || [];
                // Merge monitored gateways into entities and map customSettings to top-level
                state.monitored.forEach(gateway => {
                    const mappedGateway = {
                        ...gateway,
                        mapId: (gateway as any).mapId && typeof (gateway as any).mapId === 'object' && '_id' in (gateway as any).mapId ? (gateway as any).mapId._id : gateway.mapId,
                        viewRadius: gateway.customSettings?.viewRadius || gateway.viewRadius,
                        logicThreshold: gateway.customSettings?.logicThreshold || gateway.logicThreshold || gateway.maxRSSI,
                    };
                    state.entities[gateway.mac] = mappedGateway;
                });
            })
            .addCase(addGatewayToMonitoring.fulfilled, (state, action) => {
                // Move gateway from available to monitored
                state.available = state.available.filter(g => g.mac !== action.payload.gateway.mac);
                const existingIndex = state.monitored.findIndex(g => g.mac === action.payload.gateway.mac);
                if (existingIndex >= 0) {
                    state.monitored[existingIndex] = action.payload.gateway;
                } else {
                    state.monitored.push(action.payload.gateway);
                }
                // Update entities
                state.entities[action.payload.gateway.mac] = action.payload.gateway;
            })
            .addCase(removeGatewayFromMonitoring.fulfilled, (state, action) => {
                // Move gateway from monitored to available if still in API
                state.monitored = state.monitored.filter(g => g.mac !== action.payload.gateway.mac);
                delete state.entities[action.payload.gateway.mac];
                if (action.payload.gateway.isAvailableInAPI) {
                    const existingIndex = state.available.findIndex(g => g.mac === action.payload.gateway.mac);
                    if (existingIndex >= 0) {
                        state.available[existingIndex] = action.payload.gateway;
                    } else {
                        state.available.push(action.payload.gateway);
                    }
                }
            });
    },
});

export const { setGateways, addGateway, updateGateway, removeGateway, updateGatewayPosition, resetGatewaysState } = gatewaysSlice.actions;
export default gatewaysSlice.reducer;
